import { Link } from "react-router-dom";
import { FiExternalLink, FiShare2 } from "react-icons/fi";
import utils from "../../helpers/utils";
import { useEffect, useState, useCallback } from "react";
import Strings from "../../constants/Strings";
import { useAuth } from "../../context/AuthContext";
import linksthree from "../../services/linksthree";
import { toast } from "react-toastify";
import { LinkThreeData, Professional } from "../../types";
import AvailabilityTab from "../../components/profile/availability/AvailabilityTab";
import ServiceTab from "../../components/profile/services/ServiceTab";
import AdvertisementTab from "../../components/profile/services/AdvertisementTab";
import ProfileForm from "../../components/profile/ProfileForm";
import ProfileTabs from "../../components/profile/ProfileTabs";
import LinksCard from "../../components/profile/links/LinksCard";
import LinksManagementModal from "../../components/profile/links/LinksManagementModal";

function Perfil() {
  const { getUser } = useAuth();
  const user = getUser();
  const isAdmin = !!user?.is_admin;
  const [data, setData] = useState<LinkThreeData | null>(null);
  const [loading, setLoading] = useState(false);
  const [load, setLoad] = useState(true);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [modalOpen, setModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [activeTab, setActiveTab] = useState<
    "profile" | "availability" | "services" | "advertisements"
  >("profile");

  const start = useCallback(async () => {
    setLoad(true);
    const user = getUser();
    if (user?.id) {
      const result = await linksthree.getButtons(user.id);
      setData(result);
      setImagePreview(
        utils.getUriImage(result?.photo || Strings.default_photo),
      );
      const slug = result?.minha_url || user.id;
      setPreviewUrl(`${window.location.origin}/s/${slug}`);
    }
    setLoad(false);
  }, [getUser]);

  const handleSaveButtons = async (tempData: LinkThreeData) => {
    setLoading(true);
    const user = getUser();
    if (user?.id) {
      const response = await linksthree.putButton(
        user.id,
        tempData.buttons || [],
      );

      const responseColors = await linksthree.putColors(user.id, {
        background: tempData.background,
        color: tempData.color,
        color_secondary: tempData.color_secondary,
        minha_url: tempData.minha_url ?? null,
      });

      if (response && responseColors.success) {
        setData(tempData);
        toast.success("Links e cores salvos com sucesso!");
        setModalOpen(false);
        setLoading(false);
        return true;
      }
      toast.error(
        responseColors.error || "Erro ao salvar algumas informações.",
      );
      setLoading(false);
      return false;
    }
    setLoading(false);
    return false;
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    const user = getUser();
    if (user?.id && data) {
      const response = await linksthree.putSave(
        user.id,
        data as unknown as Partial<Professional>,
      );
      if (response) {
        toast.success("Alteração salva com sucesso!");
      }
    }
    setLoading(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(previewUrl);
    toast.success("Link copiado para área de transferência!");
  };

  const shareLink = () => {
    if (navigator.share) {
      navigator.share({
        title: data?.name || "Meu Perfil",
        text: "Confira meus links!",
        url: previewUrl,
      });
    } else {
      copyToClipboard();
    }
  };

  useEffect(() => {
    start();
  }, [start]);

  if (load) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-3 py-4 sm:px-4 sm:py-6 md:px-6 2xl:px-10">
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-black dark:text-white sm:text-3xl">
            Perfil
          </h2>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
            Configure seus dados pessoais e links para compartilhar com seus
            alunos.
          </p>
          <p className="mt-2 text-xs">
            <Link
              to="/pagamento"
              className="font-medium text-purple-600 hover:underline"
            >
              Pagamento e cartão
            </Link>
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => window.open(previewUrl, "_blank")}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded border border-stroke px-3 py-2 text-xs font-medium text-black transition hover:bg-gray-50 dark:border-strokedark dark:text-white dark:hover:bg-meta-4 sm:flex-initial"
          >
            <FiExternalLink size={16} />
            <span className="hidden xs:inline">Visualizar</span>
          </button>
          <button
            onClick={shareLink}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded bg-purple-600 px-3 py-2 text-xs font-medium text-white transition hover:bg-opacity-90 sm:flex-initial"
          >
            <FiShare2 size={16} />
            <span className="hidden xs:inline">Compartilhar</span>
          </button>
        </div>
      </div>

      {data && (
        <div className="flex flex-col gap-4 sm:gap-6 lg:grid lg:grid-cols-3">
          {/* Card de Links - Primeiro no mobile */}
          <div className="order-2 lg:order-2 lg:col-span-1">
            <LinksCard
              data={data}
              onManageClick={() => setModalOpen(true)}
              previewUrl={previewUrl}
            />
          </div>

          {/* Dados Pessoais - Depois no mobile */}
          <div className="order-1 lg:order-1 lg:col-span-2">
            <ProfileTabs
              activeTab={activeTab}
              onTabChange={setActiveTab}
              showAdvertisements={isAdmin}
            >
              {activeTab === "profile" ? (
                <ProfileForm
                  data={data}
                  imagePreview={imagePreview}
                  loading={loading}
                  onChange={setData}
                  onImagePreviewChange={setImagePreview}
                  onSave={handleSaveProfile}
                />
              ) : activeTab === "services" ? (
                <ServiceTab />
              ) : activeTab === "advertisements" && isAdmin ? (
                <AdvertisementTab />
              ) : (
                <AvailabilityTab />
              )}
            </ProfileTabs>
          </div>
        </div>
      )}

      {/* Modal de Gerenciamento de Links */}
      {data && (
        <LinksManagementModal
          isOpen={modalOpen}
          data={data}
          loading={loading}
          onClose={() => setModalOpen(false)}
          onSave={handleSaveButtons}
        />
      )}
    </div>
  );
}

export default Perfil;
